import { AssetInsight } from "@/api/studio/insights/types/insight.type";
import { Metric } from "@/features/reports/components/metric";
import { ClarityIcon, DigestibilityIcon, VisibilityIcon } from "@/assets/icons";
import { Progress } from "@/components/ui/progress";
import { Tabs, Tab, TabPanel } from "@/components/ui/tabs";
import { useReportsContext } from "../../contexts/reports-context";
import { PriorityAlignmentOffsetIndicator } from "@/components/ui/indicator";
import { classColors } from "@/utils/classification-mapping";
import { DigestibilityRegionTable } from "./digestibility-region-table";
import { KeyDriversWrapper } from "./key-drivers-wrapper";
import { KeyDriverMetricTab } from "./key-driver-metric-tab";
import { useSegment } from "@/hooks/use-segment";
import { AttentionMetric, AttentionSubMetrics } from "@/api/studio/insights/types/metrics.type";
import { CopilotTile } from "./copilot-tile";
import { AttentionScoreInfoModal } from "../../modals/attention-score-info-modal";

/* Prop Types */
interface AttentionDetailsProps {
  currentAsset: AssetInsight;
  hasContext: boolean;
  studyId: string;
  projectId: string;
}

/*
 * AttentionDetails component
 *
 * This component displays detailed information about the attention metrics of a given asset for the in-depth report.
 */
export const AttentionDetails = ({ currentAsset, hasContext, studyId, projectId }: AttentionDetailsProps) => {
  /* Context */
  const { state, actions } = useReportsContext();

  /* Consts */
  const hasAlignment = currentAsset?.metrics?.attention?.digestibility?.alignment !== undefined;
  const regions = currentAsset?.attention.regions;

  /* Hooks */
  const { segmentTrackEvent } = useSegment();

  /* Handlers */
  const handleContextClick = (contextId: string) => {
    // Segment Tracking
    segmentTrackEvent("Asset Report Context Selected", {
      assetId: currentAsset.assetId,
      projectId,
      studyId,
      contextId,
    });
    actions.setContextKey(contextId);
  };

  const handleMetricChange = (newMetric: AttentionSubMetrics) => {
    // Segment Tracking
    segmentTrackEvent("Asset Report Metric Changed", {
      assetId: currentAsset.assetId,
      projectId,
      studyId,
      from: state.attentionSubMetric,
      to: newMetric,
      reportType: "attention",
      parentMetric: "digestibility",
    });

    // set the new metric
    actions.setAttentionSubMetric(newMetric);
  };

  const handleDriverChange = (newMetric: AttentionMetric) => {
    // Segment Tracking
    segmentTrackEvent("Asset Report Metric Changed", {
      reportType: "attention",
      from: state.attentionMetric,
      to: newMetric,
      assetId: currentAsset.assetId,
      studyId,
      projectId,
    });

    // set the new metric
    actions.setAttentionMetric(newMetric);
  };

  /* Render */
  return (
    <>
      <div className="px-4 py-2 my-4">
        <Metric value={currentAsset?.metrics?.attention.score} title="Attention" size="xlarge" infoModal={<AttentionScoreInfoModal />} />
        <p className="text-sm text-base-content/70 mt-2">How well does the asset capture the viewer&apos;s attention?</p>
      </div>

      {/* Key Drivers */}
      <KeyDriversWrapper description="The primary elements influencing overall Attention score:">
        <div className="flex flex-row gap-2">
          {hasContext && (
            <KeyDriverMetricTab
              onClick={() => handleDriverChange("visibility")}
              metric="visibility"
              metricIcon={<VisibilityIcon className="size-4 flex-shrink-0" />}
              currentAsset={currentAsset}
              reportType="attention"
            />
          )}
          <KeyDriverMetricTab
            onClick={() => handleDriverChange("clarity")}
            metric="clarity"
            metricIcon={<ClarityIcon className="size-4 flex-shrink-0" />}
            currentAsset={currentAsset}
            reportType="attention"
          />
          <KeyDriverMetricTab
            onClick={() => handleDriverChange("digestibility")}
            metric="digestibility"
            metricIcon={<DigestibilityIcon className="size-4 flex-shrink-0" />}
            currentAsset={currentAsset}
            reportType="attention"
          />
        </div>
      </KeyDriversWrapper>

      {/* Copilot */}
      <CopilotTile metric={state.attentionMetric} />

      {/* Sub Drivers */}
      <div className="p-4 rounded-lg bg-base-200 flex flex-col gap-3 mt-4">
        {state.attentionMetric === "visibility" && hasContext && (
          <>
            <p className="font-semibold">Visibility drivers</p>
            <p className="text-xs text-base-content/70">How effectively does your creative win attention within your target contexts?</p>

            <div>
              {currentAsset?.attention.contexts?.map((context, index) => (
                <div
                  key={context.key}
                  className={`flex flex-row justify-between items-center p-3 gap-2 bg-base-100 border-2 border-base-200 cursor-pointer 
                    ${index === 0 ? "rounded-t-lg" : index === (currentAsset?.attention.contexts?.length ?? 0) - 1 ? "rounded-b-lg" : ""} 
                    ${state.contextKey === context.key ? "border-2 border-primary rounded-lg bg-base-200" : "hover:bg-base-100/40"}`}
                  onClick={() => handleContextClick(context.key)}
                >
                  <p className="text-sm font-semibold flex-shrink-0">{context.label}</p>

                  <div className="flex flex-row items-center gap-5">
                    <Progress value={context.vis} className="w-15" />
                    <span className="text-sm font-medium ml-2">{context.vis}%</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {state.attentionMetric === "clarity" && (
          <>
            <p className="font-semibold">Clarity drivers</p>
            <p className="text-xs text-base-content/70">The primary elements influencing overall Clarity score:</p>

            <div>
              <div className="flex flex-row justify-between items-center p-3 gap-2 bg-base-100 border-2 border-base-200 cursor-pointer rounded-t-lg">
                <p className="text-sm font-semibold flex-shrink-0">Number of hotspots</p>
                <span className="text-sm font-medium ml-2">{currentAsset?.metrics?.attention?.clarity?.numHotspots}</span>
              </div>
              <div className="flex flex-row justify-between items-center p-3 gap-2 bg-base-100 border-2 border-base-200 cursor-pointer rounded-b-lg">
                <p className="text-sm font-semibold flex-shrink-0">Hotspot prominence</p>
                <div className="flex flex-row items-center gap-5">
                  <Progress value={currentAsset?.metrics?.attention?.clarity?.prominence} className="w-15" />
                  <span className="text-sm font-medium ml-2">{currentAsset?.metrics?.attention?.clarity?.prominence}%</span>
                </div>
              </div>
            </div>
          </>
        )}
        {state.attentionMetric === "digestibility" && (
          <>
            <p className="font-semibold">Digestibility drivers</p>
            <p className="text-xs text-base-content/70">How effectively does the design draw attention to key information?</p>
            <div>
              <div className="flex flex-row justify-between items-center p-3 gap-2 bg-base-100 border-2 border-base-200 cursor-pointer rounded-t-lg">
                <p className="text-sm font-semibold flex-shrink-0">Number of AOIs</p>
                <span className="text-sm font-medium ml-2">{currentAsset?.metrics?.attention?.digestibility.numRegions}</span>
              </div>
              <div className={`flex flex-row justify-between items-center p-3 gap-2 bg-base-100 border-2 border-base-200 cursor-pointer ${hasAlignment ? "" : "rounded-b-lg"}`}>
                <p className="text-sm font-semibold flex-shrink-0">AOI prominence</p>
                <div className="flex flex-row items-center gap-5">
                  <Progress value={currentAsset?.metrics?.attention?.digestibility?.prominence} className="w-15" />
                  <span className="text-sm font-medium ml-2">{currentAsset?.metrics?.attention?.digestibility?.prominence}%</span>
                </div>
              </div>
              {hasAlignment && currentAsset?.metrics?.attention?.digestibility?.alignment && (
                <div className="flex flex-row justify-between items-center p-3 gap-2 bg-base-100 border-2 border-base-200 cursor-pointer rounded-b-lg">
                  <p className="text-sm font-semibold flex-shrink-0">Priority alignment</p>
                  <div className="flex flex-row items-center gap-5">
                    <Progress value={currentAsset?.metrics?.attention?.digestibility?.alignment} className="w-15" />
                    <span className="text-sm font-medium ml-2">{currentAsset?.metrics?.attention?.digestibility?.alignment}%</span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Digestibility AOI Elements */}
      {state.attentionMetric === "digestibility" && (
        <div className="p-4 rounded-lg bg-base-200 flex flex-col gap-3 mt-4">
          <p className="font-semibold">AOI elements</p>
          <p className="text-xs text-base-content/70">The percentage of viewers likely to notice an AOI element within the first 2 seconds of viewing.</p>
          <div className="flex flex-col gap-4">
            <Tabs
              defaultTab={hasAlignment ? "pa" : "pop"}
              onTabChange={(tab) => {
                handleMetricChange(tab as AttentionSubMetrics);
              }}
              variant="lifted"
            >
              {/* Priority Alignment Tab */}
              {hasAlignment && (
                <Tab value="pa" className="tab-active:!bg-primary tab-active:!text-primary-content">
                  PA
                </Tab>
              )}

              {/* Sub Metrics Tabs */}
              <Tab value="pop" className="tab-active:bg-primary tab-active:text-primary-content">
                POP
              </Tab>

              <Tab value="soa">SOA</Tab>

              <Tab value="pow">POW</Tab>

              {/* Priority Alignment Panel */}
              {hasAlignment && (
                <TabPanel value="pa" className="tab-content bg-base-100 border-base-300 rounded-b-xl p-6 h-full">
                  <p className="font-bold text-sm mb-4">Priority alignment (PA)</p>
                  <p className="text-sm opacity-70 mb-4">How elements are prioritised and what order they are likely seen by your audience:</p>
                  <div className="flex flex-col h-full mt-4">
                    {/* Group regions by priority - show all priority levels 0-4 */}
                    {[1, 2, 3, 4, 0].map((priority) => {
                      const priorityRegions = regions.filter((region) => region.priority === priority);
                      const priorityLabels = ["First priority", "Second priority", "Third priority", "Fourth priority"];
                      const priorityLabel = priority === 0 ? "Unprioritized" : priorityLabels[priority - 1];

                      // Only render priority sections that have regions
                      if (priorityRegions.length === 0) return null;

                      return (
                        <div key={priority} className="mb-6">
                          <div className="rounded-t-lg bg-base-200 px-4 py-3 grid grid-cols-[16px_3fr_1fr_1.5fr] gap-4 items-center">
                            <p className="text-sm opacity-60 font-bold col-span-2">{priorityLabel}</p>
                            <p className="text-sm opacity-60 font-bold text-right">Gaze</p>
                            <p className="text-sm opacity-60 font-bold text-right">Alignment</p>
                          </div>
                          <div className="flex flex-col">
                            {priorityRegions.map((region, i) => (
                              <div key={i} className="grid grid-cols-[16px_3fr_1fr_1.5fr] gap-4 items-center py-3 justify-between border-b px-4 border-base-300">
                                <div className="size-4 rounded-xs" style={{ backgroundColor: classColors[region.classification] }} />
                                <div className="flex items-center truncate">
                                  <span className="text-sm font-semibold truncate">{region.label}</span>
                                </div>
                                <div className="text-right">
                                  <span className="text-sm font-semibold">{region.rank}</span>
                                </div>
                                <div className="flex items-center justify-end">
                                  <PriorityAlignmentOffsetIndicator offset={region.offset ?? 0} />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </TabPanel>
              )}

              {/* POP Panel */}
              <TabPanel value="pop" className="tab-content bg-base-100 border-base-300 rounded-b-xl p-6 h-full">
                <p className="font-bold text-sm mb-4">Probability of Perception (POP)</p>
                <p className="text-sm opacity-70 mb-4">The percentage of viewers likely to notice an AOI within the first 2 seconds of viewing.</p>
                <DigestibilityRegionTable regions={regions} metric="pop" />
              </TabPanel>

              {/* SOA Panel */}
              <TabPanel value="soa" className="tab-content bg-base-100 border-base-300 rounded-b-xl p-6 h-full">
                <p className="font-bold text-sm mb-4">Share of Attention (SOA)</p>
                <p className="text-sm opacity-70 mb-4">The percentage share of attention an AOI receives within the first 2 seconds of viewing.</p>
                <DigestibilityRegionTable regions={regions} metric="soa" />
              </TabPanel>

              {/* POW Panel */}
              <TabPanel value="pow" className="tab-content bg-base-100 border-base-300 rounded-b-xl p-6 h-full">
                <p className="font-bold text-sm mb-4">Attention Power Factor (POW)</p>
                <p className="text-sm opacity-70 mb-4">The amount of attention an AOI receives relative to the amount of space it occupies.</p>
                <DigestibilityRegionTable regions={regions} metric="pow" />
              </TabPanel>
            </Tabs>
          </div>
        </div>
      )}
    </>
  );
};
